/**
 * AboutPage
 * The story behind Wildy: mission, values, why wildlife matters, and the two founders.
 * Built with the same patterns as What we offer and For business (design-system/README.md › Page patterns):
 * hero → white / sage section rhythm → closing with one pointer for operators → Footer.
 * Conservation facts are about the world, not claims about Wildy's own impact; keep them that way.
 */

import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Globe, Heart, Mail, Quote, Skull, Sprout, Store } from 'lucide-react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Filler,
} from 'chart.js';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { Header } from '@/components/Header';
import { HeroSection } from '@/components/homepage/HeroSection';
import { Footer } from '@/components/homepage/Footer';
import { publicContact } from '@/config/publicContact';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Filler);

// Chart colours come from the design tokens: forest for the data line, slate for the extinction bars.
const FOREST = '#2C5F2D';
const SLATE = '#64748B';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4 },
};

interface Species {
  name: string;
  scientific: string;
  category: string;
  icon: string;
}

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

function FounderCard({ id, photo, email }: { id: 'mario' | 'guillermo'; photo: string; email: string }) {
  const { t } = useTranslation('public', { keyPrefix: 'about.founders' });
  const tags = t(`${id}.tags`, { returnObjects: true });
  return (
    <motion.article {...fadeUp} className="flex flex-col rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="relative h-64 sm:h-80 bg-brand-100">
        <img src={photo} alt={t(`${id}.name`)} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      </div>
      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <h3 className="text-2xl font-bold text-brand-900 tracking-tight">{t(`${id}.name`)}</h3>
        <ul className="mt-3 flex flex-wrap gap-2">
          {Object.values(typeof tags === 'object' && tags ? tags as Record<string, string> : {}).map(tag => (
            <li key={tag} className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-sm text-brand-800">{tag}</li>
          ))}
        </ul>
        <p className="mt-5 text-slate-600 leading-relaxed">{t(`${id}.bio`)}</p>
        <blockquote className="mt-5 rounded-lg bg-brand-50 border-l-4 border-primary p-5">
          <Quote className="w-5 h-5 text-primary mb-2" aria-hidden />
          <p className="text-lg font-medium text-brand-900 leading-relaxed">{t(`${id}.quote`)}</p>
        </blockquote>
        <a
          href={`mailto:${email}`}
          className="mt-auto pt-6 inline-flex items-center gap-2 font-semibold text-primary hover:underline underline-offset-4"
        >
          <Mail className="w-4 h-4" aria-hidden />
          {email}
        </a>
      </div>
    </motion.article>
  );
}

export function AboutPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('public', { keyPrefix: 'about' });

  const success = 'environmentalFacts.conservationSuccess';
  const species = asArray<Species>(t(`${success}.flipCard.species`, { returnObjects: true }));
  const categories = t(`${success}.flipCard.categories`, { returnObjects: true }) as Record<string, string>;
  const values = [
    { key: 'conservation' },
    { key: 'community' },
    { key: 'experience' },
  ] as const;
  const impacts = [
    { key: 'tourism', emoji: '🦍' },
    { key: 'education', emoji: '🐢' },
  ] as const;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <HeroSection
        hideSearch
        hideFilters
        videoSrc="/video/hero-section-aurora-video-background.mp4"
        customContent={
          // Same header clearance as What we offer: VideoHero pads phones itself; add 4rem + safe area from sm up.
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 sm:pt-[calc(4rem+env(safe-area-inset-top,0px))] text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1
                className="text-[2rem] sm:text-6xl font-bold text-white tracking-tight leading-[1.1] sm:leading-[1.05]"
                style={{ textShadow: '0 4px 20px rgba(0,0,0,0.45)' }}
              >
                <span className="block">{t('hero.title.discover')}</span>
                <span className="block mt-2 bg-gradient-to-r from-action-200 to-action-300 bg-clip-text text-transparent">
                  {t('hero.title.adventures')}
                </span>
              </h1>
              <p
                className="mt-4 sm:mt-6 mx-auto max-w-2xl text-base sm:text-xl text-white/95 leading-relaxed"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              >
                {t('hero.subtitle.main')}
                <span className="hidden sm:block mt-2 text-lg text-white/85">{t('hero.subtitle.conservation')}</span>
              </p>
              <div className="mt-6 sm:mt-8 flex justify-center">
                <Button size="lg" onClick={() => navigate('/what-we-offer')} className="group">
                  {t('cta.buttons.findAdventures')}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden />
                </Button>
              </div>
            </motion.div>
          </div>
        }
      />

      {/* Mission: vision and impact */}
      <section aria-labelledby="mission-title" className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="mission-title" eyebrow={t('mission.badge')} title={t('mission.title')} />
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            {([['vision', 'text1', Globe], ['purpose', 'text2', Heart]] as const).map(([title, text, Icon], i) => (
              <motion.div key={title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-lg border border-brand-200 bg-white p-6 sm:p-8 shadow-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-50 text-primary mb-5">
                  <Icon className="w-5 h-5" aria-hidden />
                </span>
                <h3 className="text-2xl font-bold text-brand-900 tracking-tight mb-3">{t(`mission.${title}`)}</h3>
                <p className="text-lg text-slate-600 leading-relaxed">{t(`mission.${text}`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section aria-labelledby="values-title" className="py-16 sm:py-24 bg-brand-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="values-title" eyebrow={t('values.eyebrow')} title={t('values.sectionTitle')} description={t('values.sectionSubtitle')} />
          <ul className="grid gap-4 sm:gap-6 md:grid-cols-3">
            {values.map(({ key }, i) => (
              <motion.li key={key} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-2xl mb-4" aria-hidden>
                  {t(`values.${key}.emoji`)}
                </span>
                <h3 className="text-lg font-semibold text-brand-900 mb-2">{t(`values.${key}.title`)}</h3>
                <p className="text-slate-600 leading-relaxed">{t(`values.${key}.description`)}</p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why it matters: the state of wildlife, with sources */}
      <section aria-labelledby="facts-title" className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="facts-title" eyebrow={t('environmentalFacts.eyebrow')} title={t('environmentalFacts.title')} description={t('environmentalFacts.subtitle')} />
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
            <motion.figure {...fadeUp} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-brand-900">{t('environmentalFacts.threatenedSpecies.title')}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{t('environmentalFacts.threatenedSpecies.description')}</p>
              <div className="mt-6 h-[260px] sm:h-[300px]">
                <Line
                  aria-label={t('environmentalFacts.threatenedSpecies.chart.title')}
                  data={{
                    labels: ['2005', '2010', '2015', '2020', '2023'],
                    datasets: [{
                      label: t('environmentalFacts.threatenedSpecies.chart.title'),
                      data: [30000, 35000, 38000, 41000, 42100],
                      backgroundColor: 'rgba(44, 95, 45, 0.12)',
                      borderColor: FOREST,
                      pointBackgroundColor: FOREST,
                      borderWidth: 2,
                      fill: true,
                      tension: 0.3,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      y: { beginAtZero: false, title: { display: true, text: t('environmentalFacts.threatenedSpecies.chart.yAxis') } },
                      x: { title: { display: true, text: t('environmentalFacts.threatenedSpecies.chart.xAxis') } },
                    },
                  }}
                />
              </div>
              <figcaption className="mt-4 text-sm text-slate-500">{t('environmentalFacts.threatenedSpecies.source')}</figcaption>
            </motion.figure>

            <motion.figure {...fadeUp} transition={{ duration: 0.4, delay: 0.08 }} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold text-brand-900 flex items-center gap-2">
                <Skull className="w-5 h-5 text-slate-500" aria-hidden />
                {t('environmentalFacts.extinctSpecies.title')}
              </h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{t('environmentalFacts.extinctSpecies.description')}</p>
              <div className="mt-6 h-[260px] sm:h-[300px]">
                <Bar
                  aria-label={t('environmentalFacts.extinctSpecies.chart.title')}
                  data={{
                    labels: ['1500-1599', '1600-1699', '1700-1799', '1800-1899', '1900-1999', '2000-2099'],
                    datasets: [{
                      label: t('environmentalFacts.extinctSpecies.chart.label'),
                      data: [5, 8, 15, 40, 120, 50],
                      backgroundColor: 'rgba(100, 116, 139, 0.25)',
                      borderColor: SLATE,
                      borderWidth: 2,
                      borderRadius: 4,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: { callbacks: { label: ctx => `${ctx.parsed.y} ${t('environmentalFacts.extinctSpecies.chart.yAxis')}` } },
                    },
                    scales: {
                      y: { beginAtZero: true, title: { display: true, text: t('environmentalFacts.extinctSpecies.chart.yAxis') } },
                      x: { title: { display: true, text: t('environmentalFacts.extinctSpecies.chart.xAxis') } },
                    },
                  }}
                />
              </div>
              <figcaption className="mt-4 text-sm text-slate-500">{t('environmentalFacts.extinctSpecies.source')}</figcaption>
            </motion.figure>
          </div>
        </div>
      </section>

      {/* Conservation success: species brought back, and two real examples */}
      <section aria-labelledby="success-title" className="py-16 sm:py-24 bg-brand-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            id="success-title"
            eyebrow={t(`${success}.title`)}
            title={t(`${success}.subtitle`)}
            description={t(`${success}.description`)}
          />

          <motion.div {...fadeUp} className="rounded-lg border border-brand-200 bg-white p-6 sm:p-8 shadow-sm grid gap-8 lg:grid-cols-[2fr_3fr] lg:items-center">
            <div className="text-center lg:text-left">
              <p className="text-6xl sm:text-7xl font-bold text-primary tracking-tight">200+</p>
              <h3 className="mt-3 text-2xl font-bold text-brand-900 tracking-tight">{t(`${success}.flipCard.title`)}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{t(`${success}.protected`)}</p>
            </div>
            <div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {species.map(s => (
                  <li key={s.scientific} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
                    <span className="text-2xl" aria-hidden>{s.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-brand-900 leading-tight">{s.name}</span>
                      <span className="block text-sm italic text-slate-500 leading-tight truncate">{s.scientific}</span>
                    </span>
                    <span className="hidden sm:inline rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-800">
                      {categories?.[s.category]}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-slate-500">{t(`${success}.flipCard.footerNote`)}</p>
            </div>
          </motion.div>

          <h3 className="mt-16 text-center text-2xl sm:text-3xl font-bold text-brand-900 tracking-tight [text-wrap:balance]">
            {t(`${success}.impactCards.title`)}
          </h3>
          <p className="mt-3 mb-8 text-center text-lg text-slate-600 max-w-2xl mx-auto">{t(`${success}.impactCards.description`)}</p>
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            {impacts.map(({ key, emoji }, i) => (
              <motion.article key={key} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-lg border border-brand-200 bg-white p-6 sm:p-8 shadow-sm">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-2xl" aria-hidden>{emoji}</span>
                  <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-800">
                    {t(`${success}.impactCards.${key}.badge`)}
                  </span>
                </div>
                <h4 className="text-xl font-semibold text-brand-900">{t(`${success}.impactCards.${key}.cardTitle`)}</h4>
                <p className="mt-2 font-medium text-slate-700 leading-relaxed">{t(`${success}.impactCards.${key}.front`)}</p>
                <p className="mt-3 text-slate-600 leading-relaxed">{t(`${success}.impactCards.${key}.back`)}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section aria-labelledby="founders-title" className="py-16 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader id="founders-title" eyebrow={t('founders.eyebrow')} title={t('founders.title')} description={t('founders.subtitle')} />
          <div className="grid gap-6 md:grid-cols-2">
            <FounderCard id="mario" photo="/images/mario.jpg" email={publicContact.founders.marioEmail} />
            <FounderCard id="guillermo" photo="/images/guillermo.jpg" email={publicContact.founders.guillermoEmail} />
          </div>
        </div>
      </section>

      {/* Closing: explore, plus the pointers for operators and for anyone who wants to talk */}
      <section className="py-16 sm:py-24 bg-brand-50">
        <motion.div {...fadeUp} className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <Sprout className="w-8 h-8 text-primary mx-auto mb-4" aria-hidden />
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-900 tracking-tight [text-wrap:balance]">{t(`${success}.impactCards.ctaTitle`)}</h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">{t(`${success}.impactCards.ctaDescription`)}</p>
          <Button size="lg" onClick={() => navigate('/what-we-offer')} className="group mt-8">
            {t(`${success}.impactCards.ctaButton`)}
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden />
          </Button>
          <p className="mt-6 text-slate-600">
            {t('closing.contact')}{' '}
            <Link to="/contact" className="font-semibold text-primary hover:underline underline-offset-4">{t('closing.contactLink')}</Link>
          </p>
          <div className="mt-12 rounded-lg border border-brand-200 bg-white p-5 sm:p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-primary">
              <Store className="w-5 h-5" aria-hidden />
            </span>
            <p className="flex-1 text-slate-700 leading-relaxed">
              <span className="font-semibold text-brand-900">{t('closing.operatorsTitle')}</span>{' '}
              {t('closing.operatorsDescription')}
            </p>
            <Link
              to="/for-business"
              className="inline-flex items-center gap-1 font-semibold text-primary hover:underline underline-offset-4 shrink-0"
            >
              {t('closing.operatorsLink')}
              <ArrowRight className="w-4 h-4" aria-hidden />
            </Link>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
